import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { Table } from 'app-components';
import {
    fetchDmiVerificationsData,
    loadUploadedVerificationDocuments,
    deleteUploadedVerificationDocuments,
} from '@actions/dmi/dmiActionCreators';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import withModalWrapper from '@containers/common/WithModalWrapper';
import { configs } from '@constants/configs/configs';
import { useAppDispatch, useAppSelector, useModal } from '@hooks/index';
import { environmentSelector } from '@selectors/commonSelectors';
import { VERIFICATION } from '../constants';
import { modalConfig } from '../modals/modalConfig';

const StyledVerificationsTable = styled.div`
    * {
        border: 0 !important;
    }

    .usa-table--borderless th:first-child {
        padding-left: 10px;
    }

    thead {
        tr {
            background-color: ${({ theme }) => theme.gray10};
        }
    }

    tbody {
        tr:nth-child(odd) {
            background-color: ${({ theme }) => theme.white};
        }

        tr:nth-child(even) {
            background-color: ${({ theme }) => theme.gray11};
        }
    }
`;

const StyledLink = styled.a`
    color:  ${({ theme }) => theme.primaryMain} !important;
    cursor: pointer;
    overflow-wrap: anywhere;
  `;

const VerificationsTable = ({ documentCategoryDetailList, documentCategory, applicantId }) => {
    const { openModal, closeModal } = useModal();
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const environment = useAppSelector(environmentSelector);
    const { userPermissionList } = useAppSelector(state => state.capApplications.capApplicationsInfo);
    const { language } = useAppSelector(state => state.common.environment);
    const {
        data: { caseId: caseNumber },
    } = useAppSelector(state => state.dmi.verificationsData);

    const documentDisplayHandler = docPath => {
        const encodedDocPath = window.encodeURI(docPath);
        window.open(
            configs.endpoints.DMI_VIEW_UPLOADED_DOCUMENT_ENDPOINT + encodedDocPath,
            '_blank',
            'directories=no, status=no, menubar=no, scrollbars=yes, resizable=no,width=600, height=280,top=200,left=200',
        );
    };

    const hasPermission = (permission: string): boolean => userPermissionList.includes(permission);

    const switchCSR = encryptedID => {
        var encodedticketID = window.encodeURI(encryptedID);
        window.location.href = configs.endpoints.DMI_VIEW_TICKET_ENDPOINT + encodedticketID;
    };

    const onDeleteHandler = docPath => {
        dispatch(
            deleteUploadedVerificationDocuments(
                configs.endpoints.DMI_DELETE_UPLOADED_DOCUMENT_ENDPOINT.processReplacements({ documentId: docPath }),
                data => {
                    if (data?.Status === VERIFICATION.SUCCESS) {
                        closeModal('deleteDocumentModal');
                        dispatch(
                            loadUploadedVerificationDocuments(
                                configs.endpoints.DMI_LOAD_DOCUMENT_ENDPOINT,
                                {
                                    applicantId: applicantId,
                                    documentCategory: documentCategory,
                                },
                                data => {},
                            ),
                        );
                        dispatch(
                            fetchDmiVerificationsData(
                                configs.endpoints.DMI_GET_DMI_VERIFICATIONS.processReplacements({
                                    caseNumber,
                                    language,
                                }),
                                environment,
                            ),
                        );
                    }
                },
            ),
        );
    };

    const documentDeleteHandler = docPath => {
        openModal('deleteDocumentModal', { onDeleteHandler: () => onDeleteHandler(docPath) });
    };

    const tableColumns = [
        { titleId: 'common.documentType', key: 'documentType' },
        {
            titleId: 'common.documentName',
            key: 'documentName',
            render: item => {
                return (
                    <>
                        <ConditionalRendering isTrue={item.showDocument}>
                            <StyledLink onClick={() => documentDisplayHandler(item.encryptedDocPath)}>
                                {item.documentName}
                            </StyledLink>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={!item.showDocument}>{item.documentName}</ConditionalRendering>
                        <ConditionalRendering isTrue={item.isDeletable}>
                            <StyledLink
                                title={intl.formatMessage({ id: 'dmi.button.delete.file' })}
                                onClick={() => documentDeleteHandler(item.encryptedDocPath)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} className="margin-l-6" />
                            </StyledLink>
                        </ConditionalRendering>
                    </>
                );
            },
        },
        { titleId: 'common_cec_status', key: 'status' },
        ...(hasPermission('TKM_VIEW_TICKET')
            ? [
                {
                    titleId: 'common.ticket',
                    key: 'ticketNumber',
                    render: item => {
                        return (
                            <StyledLink onClick={() => switchCSR(item.encryptedID)}>{item.ticketNumber}</StyledLink>
                        );
                    },
                },
            ]
            : []),
    ];

    return (
        <StyledVerificationsTable data-testid={`verificationsTable-${applicantId}-${documentCategory}`}>
            <Table
                columns={tableColumns}
                data={documentCategoryDetailList.map(data => ({
                    documentType: data?.consumerDocument?.documentType,
                    documentName: data?.consumerDocument?.documentName,
                    status: data?.consumerDocument?.accepted,
                    encryptedID: data?.encryptedID,
                    encryptedDocPath: data?.encryptedDocPath,
                    isDeletable: data?.isDeletable,
                    showDocument: data?.showDocument,
                    ticketNumber: data?.ticketNumber || false,
                }))}
                hasBorders={false}
            />
        </StyledVerificationsTable>
    );
};

export default withModalWrapper(VerificationsTable, modalConfig);
